<template>
  <div
    class="company-handler"
    :class="{ selected: isSelected }"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >

    <!-- LOGO -->

    <div class="company-logo-shell">

      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="`${displayName} logo`"
        class="company-logo"
        @error="handleLogoError"
      />

      <div
        v-else
        class="company-logo company-logo-placeholder"
      >
        {{ companyInitial }}
      </div>

    </div>


    <!-- INFO -->

    <div class="company-info">

      <h3 class="company-name">

        <template
          v-for="(part, index) in highlightedName"
          :key="`name-${index}`"
        >

          <mark
            v-if="part.highlighted"
            class="search-highlight"
          >
            {{ part.text }}
          </mark>

          <span v-else>
            {{ part.text }}
          </span>

        </template>

      </h3>


      <p class="company-specifics">

        <template
          v-for="(part, index) in highlightedSpecifics"
          :key="`specifics-${index}`"
        >

          <mark
            v-if="part.highlighted"
            class="search-highlight"
          >
            {{ part.text }}
          </mark>

          <span v-else>
            {{ part.text }}
          </span>

        </template>

      </p>

    </div>


    <!-- SELECTION -->

    <div
      class="selection-icon"
      :class="{ selected: isSelected }"
    >

      <template v-if="isSelected">

        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5.3 10.1L8.4 13.2L14.8 6.8"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

      </template>


      <template v-else>

        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M7.5 5.5L12 10L7.5 14.5"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

      </template>

    </div>

  </div>
</template>


<script setup>

import {
  computed,
  defineEmits,
  defineProps,
  ref
} from "vue";


const props =
  defineProps({

    name: {
      type: String,
      default: ""
    },

    specifics: {
      type: String,
      default: ""
    },

    logoUrl: {
      type: String,
      default: ""
    },

    companyId: {
      type: String,
      required: true
    },

    isSelected: {
      type: Boolean,
      default: false
    },

    searchQuery: {
      type: String,
      default: ""
    }

  });


const emit =
  defineEmits([
    "companyClicked"
  ]);


const logoFailed =
  ref(false);


/* ============================================================
   DISPLAY
============================================================ */

const displayName =
  computed(() => {

    return (
      props.name.trim() ||
      "Неизвестная компания"
    );

  });


const displaySpecifics =
  computed(() => {

    return (
      props.specifics.trim() ||
      "Специфика не указана"
    );

  });


const companyInitial =
  computed(() => {

    return displayName.value
      .charAt(0)
      .toUpperCase();

  });


/* ============================================================
   HIGHLIGHT
============================================================ */

const escapeRegExp =
  (value) => {

    return value.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

  };


const highlightText =
  (
    text,
    query
  ) => {

    const source =
      String(
        text ?? ""
      );


    const normalizedQuery =
      String(
        query ?? ""
      ).trim();


    if (!normalizedQuery) {

      return [
        {
          text: source,
          highlighted: false
        }
      ];

    }


    const searchParts =
      normalizedQuery
        .split(/\s+/)
        .map(
          item =>
            item.trim()
        )
        .filter(Boolean)
        .map(escapeRegExp)
        .sort(
          (
            first,
            second
          ) =>
            second.length -
            first.length
        );


    if (!searchParts.length) {

      return [
        {
          text: source,
          highlighted: false
        }
      ];

    }


    const regex =
      new RegExp(
        `(${searchParts.join("|")})`,
        "gi"
      );


    return source
      .split(regex)
      .map(
        (
          part,
          index
        ) => ({
          text: part,
          highlighted:
            index % 2 === 1
        })
      )
      .filter(
        part =>
          part.text.length > 0
      );

  };


const highlightedName =
  computed(() => {

    return highlightText(
      displayName.value,
      props.searchQuery
    );

  });


const highlightedSpecifics =
  computed(() => {

    return highlightText(
      displaySpecifics.value,
      props.searchQuery
    );

  });


/* ============================================================
   ACTIONS
============================================================ */

const handleClick =
  () => {

    emit(
      "companyClicked",
      props.companyId
    );

  };


const handleLogoError =
  () => {

    logoFailed.value =
      true;

  };

</script>


<style scoped>

/* ============================================================
   CARD
============================================================ */

.company-handler {
  width: 100%;
  min-width: 0;
  min-height: 76px;

  display: flex;
  align-items: center;

  gap: 12px;

  box-sizing: border-box;

  padding:
    11px
    12px;

  color:
    #2f343b;

  background:
    rgba(255, 255, 255, 0.82);

  border:
    1px solid
    rgba(30, 45, 65, 0.055);

  border-radius:
    15px;

  box-shadow:
    0
    5px
    16px
    rgba(15, 23, 42, 0.025);

  cursor:
    pointer;

  outline:
    none;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease,
    transform .18s ease;
}


.company-handler:hover {
  background:
    #ffffff;

  border-color:
    rgba(55, 127, 228, 0.11);

  box-shadow:
    0
    8px
    20px
    rgba(15, 23, 42, 0.045);

  transform:
    translateY(-1px);
}


.company-handler:active {
  transform:
    scale(.993);
}


.company-handler:focus-visible {
  border-color:
    rgba(55, 127, 228, 0.45);

  box-shadow:
    0
    0
    0
    3px
    rgba(55, 127, 228, 0.09);
}


/* ============================================================
   SELECTED
============================================================ */

.company-handler.selected {
  background:
    linear-gradient(
      135deg,
      rgba(55, 127, 228, 0.075),
      rgba(55, 127, 228, 0.025)
    );

  border-color:
    rgba(55, 127, 228, 0.16);

  box-shadow:
    inset
    0
    0
    0
    1px
    rgba(55, 127, 228, 0.018);
}


/* ============================================================
   LOGO SHELL
============================================================ */

.company-logo-shell {
  width: 48px;
  height: 48px;

  flex:
    0
    0
    48px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 2px;

  background:
    linear-gradient(
      145deg,
      #f5f7f9,
      #edf0f3
    );

  border:
    1px solid
    rgba(30, 45, 65, 0.055);

  border-radius:
    14px;

  box-shadow:
    0
    5px
    13px
    rgba(15, 23, 42, 0.045);
}


/* ============================================================
   LOGO
============================================================ */

.company-logo {
  width: 44px;
  height: 44px;

  display: block;

  object-fit:
    cover;

  border-radius:
    12px;
}


.company-logo-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #477dbd;

  background:
    linear-gradient(
      135deg,
      rgba(55, 127, 228, 0.10),
      rgba(80, 120, 190, 0.06)
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    16px;

  line-height:
    1;
}


/* ============================================================
   INFO
============================================================ */

.company-info {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 4px;
}


/* ============================================================
   NAME
============================================================ */

.company-name {
  width: 100%;

  margin: 0;

  overflow: hidden;

  color:
    #333941;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    13px;

  line-height:
    1.28;

  letter-spacing:
    -0.15px;

  white-space:
    nowrap;

  text-overflow:
    ellipsis;
}


/* ============================================================
   SPECIFICS
============================================================ */

.company-specifics {
  margin: 0;

  overflow: hidden;

  color:
    #888f98;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.35;

  letter-spacing:
    -0.05px;

  display:
    -webkit-box;

  -webkit-line-clamp:
    2;

  -webkit-box-orient:
    vertical;

  text-overflow:
    ellipsis;
}


/* ============================================================
   SEARCH HIGHLIGHT
============================================================ */

.search-highlight {
  margin: 0;

  padding:
    0
    2px;

  color:
    inherit;

  font:
    inherit;

  background:
    linear-gradient(
      180deg,
      transparent 12%,
      rgba(255, 208, 55, 0.58) 12%,
      rgba(255, 208, 55, 0.58) 90%,
      transparent 90%
    );

  border-radius:
    3px;

  box-decoration-break:
    clone;

  -webkit-box-decoration-break:
    clone;
}


/* ============================================================
   SELECTION
============================================================ */

.selection-icon {
  width: 30px;
  height: 30px;

  flex:
    0
    0
    30px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  color:
    #969da6;

  background:
    rgba(35, 50, 70, 0.035);

  border:
    1px solid
    rgba(35, 50, 70, 0.045);

  border-radius:
    9px;

  transition:
    color .18s ease,
    background-color .18s ease,
    border-color .18s ease,
    transform .18s ease;
}


.selection-icon svg {
  width: 15px;
  height: 15px;
}


.company-handler:hover
.selection-icon {
  color:
    #557da9;

  background:
    rgba(55, 127, 228, 0.055);

  border-color:
    rgba(55, 127, 228, 0.08);
}


.company-handler:hover
.selection-icon:not(.selected) {
  transform:
    translateX(1px);
}


.selection-icon.selected {
  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #4b8de2,
      #3774bf
    );

  border-color:
    rgba(55, 115, 195, 0.12);

  box-shadow:
    0
    5px
    12px
    rgba(55, 110, 190, 0.14);
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.company-handler {
  color:
    #e3e6ea;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0
    8px
    20px
    rgba(0, 0, 0, 0.10);
}


html[data-theme="dark"]
.company-handler:hover {
  background:
    linear-gradient(
      145deg,
      #25282e,
      #202227
    );

  border-color:
    rgba(110, 155, 215, 0.10);

  box-shadow:
    0
    10px
    24px
    rgba(0, 0, 0, 0.17);
}


html[data-theme="dark"]
.company-handler.selected {
  background:
    linear-gradient(
      145deg,
      rgba(55, 105, 165, 0.13),
      rgba(45, 75, 115, 0.07)
    );

  border-color:
    rgba(91, 158, 255, 0.17);

  box-shadow:
    inset
    0
    0
    0
    1px
    rgba(91, 158, 255, 0.018);
}


html[data-theme="dark"]
.company-logo-shell {
  background:
    linear-gradient(
      145deg,
      #292c32,
      #24262b
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0
    6px
    15px
    rgba(0, 0, 0, 0.17);
}


html[data-theme="dark"]
.company-logo-placeholder {
  color:
    #9abbea;

  background:
    rgba(91, 158, 255, 0.075);
}


html[data-theme="dark"]
.company-name {
  color:
    #e6e9ed;
}


html[data-theme="dark"]
.company-specifics {
  color:
    #888f98;
}


html[data-theme="dark"]
.search-highlight {
  background:
    linear-gradient(
      180deg,
      transparent 12%,
      rgba(210, 165, 55, 0.30) 12%,
      rgba(210, 165, 55, 0.30) 90%,
      transparent 90%
    );
}


html[data-theme="dark"]
.selection-icon {
  color:
    #707780;

  background:
    rgba(255, 255, 255, 0.025);

  border-color:
    rgba(255, 255, 255, 0.04);
}


html[data-theme="dark"]
.company-handler:hover
.selection-icon {
  color:
    #a7bad2;

  background:
    rgba(255, 255, 255, 0.045);

  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.selection-icon.selected {
  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #497fbf,
      #355f91
    );

  border-color:
    rgba(105, 155, 220, 0.10);

  box-shadow:
    0
    5px
    13px
    rgba(0, 0, 0, 0.20);
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 768px) {

  .company-handler {
    min-height:
      72px;

    padding:
      10px;

    gap:
      10px;

    border-radius:
      14px;
  }


  .company-logo-shell {
    width:
      46px;

    height:
      46px;

    flex-basis:
      46px;

    border-radius:
      13px;
  }


  .company-logo {
    width:
      42px;

    height:
      42px;

    border-radius:
      11px;
  }


  .company-name {
    font-size:
      12px;
  }


  .company-specifics {
    font-size:
      9px;
  }


  .selection-icon {
    width:
      28px;

    height:
      28px;

    flex-basis:
      28px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .company-handler,
  .selection-icon {
    transition:
      none;
  }

}

</style>